import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Palette, Plus, Eye, Edit, Trash2, Download,
  Image as ImageIcon, FileText, Layers, Crop, Grid,
  ChevronDown, ChevronRight
} from 'lucide-react';
import axiosInstance from '../../config/axios';
import { useContentFilters } from '../../hooks/useContentFilters';
import ContentFilterBar from './ContentFilterBar';
import DesignAssetCreation from '../assets/by-type/DesignAssetCreation';

interface DesignAsset {
  id: number;
  type: string;
  title: string;
  description?: string;
  status: 'draft' | 'active' | 'paused' | 'completed' | 'pending_review' | 'approved' | 'published' | 'rejected';
  metadata?: string; // JSON string containing design_type, file_format, dimensions, etc.
  scheduled_for?: string;
  created_by?: string;
  client_id?: number;
  plan_id?: number;
  created_at: string;
  updated_at?: string;
  // Parsed from metadata
  design_type?: string;
  file_format?: string;
  dimensions?: string;
  file_size?: number;
  client_name?: string;
  preview_url?: string;
  priority?: string;
  project_id?: number;
  project_name?: string;
  phase_id?: number;
  phase_name?: string;
}

export default function DesignManagement() {
  const navigate = useNavigate();
  const [designs, setDesigns] = useState<DesignAsset[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Use content filters hook
  const {
    filters,
    grouping,
    filteredItems,
    groupedItems,
    activeFilterCount,
    updateFilter,
    clearFilters,
    removeFilter,
    setGrouping,
    isGrouped,
  } = useContentFilters(designs, 'design');

  const toggleGroup = (groupKey: string) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  useEffect(() => {
    loadDesignContent();
  }, []);

  const loadDesignContent = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/content?type=design');
      if (response.data.items) {
        // Parse metadata for each design
        const parsedDesigns = response.data.items.map((design: any) => {
          let metadata = {};
          try {
            metadata = design.metadata ? JSON.parse(design.metadata) : {};
          } catch (e) {
            console.warn('Failed to parse metadata for design:', design.id);
          }
          return {
            ...design,
            design_type: metadata.design_type || 'graphic',
            file_format: metadata.file_format || 'N/A',
            dimensions: metadata.dimensions || 'N/A',
            file_size: metadata.file_size || 0,
            preview_url: metadata.preview_url || null,
            ...metadata
          };
        });
        setDesigns(parsedDesigns);
      }
    } catch (error) {
      console.error('Failed to load design content:', error);
      setDesigns([]); // Empty array on error - no mock data
    } finally {
      setLoading(false);
    }
  };

  const getDesignTypeIcon = (type?: string) => {
    if (!type) return Palette;

    switch (type.toLowerCase()) {
      case 'banner': return Crop;
      case 'template': return Layers;
      case 'graphic': return ImageIcon;
      case 'infographic': return FileText;
      case 'icons': return Grid;
      case 'illustration': return Palette;
      case 'presentation': return FileText;
      default: return Palette;
    }
  };

  const getDesignTypeBadge = (type?: string) => {
    if (!type) return 'bg-gray-100 text-gray-700';

    const colors: Record<string, string> = {
      banner: 'bg-blue-100 text-blue-700',
      template: 'bg-purple-100 text-purple-700',
      graphic: 'bg-green-100 text-green-700',
      infographic: 'bg-orange-100 text-orange-700',
      icons: 'bg-yellow-100 text-yellow-700',
      illustration: 'bg-pink-100 text-pink-700',
      presentation: 'bg-cyan-100 text-cyan-700',
    };
    return colors[type.toLowerCase()] || 'bg-gray-100 text-gray-700';
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { color: string; label: string }> = {
      draft: { color: 'bg-gray-100 text-gray-700', label: 'Draft' },
      pending_review: { color: 'bg-yellow-100 text-yellow-700', label: 'Pending Review' },
      approved: { color: 'bg-green-100 text-green-700', label: 'Approved' },
      published: { color: 'bg-blue-100 text-blue-700', label: 'Published' },
      rejected: { color: 'bg-red-100 text-red-700', label: 'Rejected' },
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / 1024 / 1024;
    return `${mb.toFixed(2)} MB`;
  };

  // Handler functions for buttons
  const handlePreview = (design: DesignAsset) => {
    // Navigate to internal content preview page
    navigate(`/dashboard/content/${design.id}/preview`);
  };

  const handleDownload = async (design: DesignAsset) => {
    try {
      // Fetch design with assets
      const response = await axiosInstance.get(`/content/${design.id}`);
      const assets = response.data.assets || [];

      if (assets.length === 0) {
        alert('No assets to download');
        return;
      }

      // Download all assets
      for (const asset of assets) {
        const link = document.createElement('a');
        link.href = `${axiosInstance.defaults.baseURL}/assets/${asset.id}/download`;
        link.download = asset.file_name;
        link.click();
        // Small delay between downloads
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    } catch (error) {
      console.error('Failed to download assets:', error);
      alert('Failed to download assets');
    }
  };

  const handleEdit = (design: DesignAsset) => {
    // Navigate to content edit page for full editing capability
    navigate(`/dashboard/content/${design.id}/edit`);
  };

  const handleDelete = async (design: DesignAsset) => {
    if (!confirm(`Are you sure you want to delete "${design.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      await axiosInstance.delete(`/content/${design.id}`);
      // Reload the list
      loadDesignContent();
    } catch (error) {
      console.error('Failed to delete design:', error);
      alert('Failed to delete design');
    }
  };

  // Mock data for filters
  const mockClients = [
    { id: 1, name: 'Acme Corporation' },
    { id: 2, name: 'TechStart Inc' },
    { id: 3, name: 'Global Services LLC' },
  ];

  const mockProjects = [
    { id: 1, name: 'Spring Campaign 2024' },
    { id: 2, name: 'Brand Refresh' },
  ];

  const mockPhases = [
    { id: 1, name: 'Creative Development' },
    { id: 2, name: 'Revisions' },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Design Assets</h1>
          <p className="mt-1 text-sm text-gray-500">
            Manage graphics, templates, and design files
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
        >
          <Plus className="h-5 w-5 mr-2" />
          Upload Design
        </button>
      </div>

      {/* Filters */}
      <ContentFilterBar
        contentType="design"
        filters={filters}
        grouping={grouping}
        activeFilterCount={activeFilterCount}
        onFilterChange={updateFilter}
        onClearFilters={clearFilters}
        onRemoveFilter={removeFilter}
        onGroupingChange={(changes) => setGrouping({ ...grouping, ...changes })}
        clients={mockClients}
        projects={mockProjects}
        phases={mockPhases}
      />

      {/* Table View */}
      {!isGrouped && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Design
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Client / Project
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    File Size
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredItems.map((design) => {
                  const TypeIcon = getDesignTypeIcon(design.design_type);
                  return (
                    <tr key={design.id} className="hover:bg-gray-50 transition">
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex-shrink-0 h-12 w-12 bg-gray-100 rounded overflow-hidden">
                            {design.preview_url ? (
                              <img
                                src={design.preview_url}
                                alt={design.title}
                                className="h-full w-full object-cover"
                              />
                            ) : (
                              <div className="h-full w-full flex items-center justify-center">
                                <Palette className="h-6 w-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">{design.title}</div>
                            <div className="text-xs text-gray-500">
                              {new Date(design.created_at).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900">{design.client_name || `Client #${design.client_id || 'N/A'}`}</div>
                        <div className="text-xs text-gray-500">{design.project_name || 'No project'}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDesignTypeBadge(design.design_type)}`}>
                          <TypeIcon className="h-3 w-3 mr-1" />
                          {design.design_type || 'Graphic'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(design.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{formatFileSize(design.file_size)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handlePreview(design)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Preview"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDownload(design)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Download"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(design)}
                            className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded"
                            title="Edit"
                          >
                            <Edit className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(design)}
                            className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded"
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grouped View */}
      {isGrouped && (
        <div className="space-y-6">
          {Object.entries(groupedItems).map(([groupName, groupDesigns]) => {
            const designsArray = Array.isArray(groupDesigns) ? groupDesigns : [];
            const isExpanded = expandedGroups.has(groupName);

            return (
            <div key={groupName} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <button
                onClick={() => toggleGroup(groupName)}
                className="w-full bg-gray-50 px-6 py-3 border-b border-gray-200 flex items-center justify-between hover:bg-gray-100 transition"
              >
                <h3 className="text-sm font-medium text-gray-900 flex items-center gap-2">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  {groupName} ({designsArray.length})
                </h3>
              </button>
              {isExpanded && (
              <div>
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Design</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Client / Project</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">File Size</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {designsArray.map((design: DesignAsset) => {
                      const TypeIcon = getDesignTypeIcon(design.design_type);
                      return (
                        <tr key={design.id} className="hover:bg-gray-50 transition">
                          <td className="px-6 py-4">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 p-2 bg-purple-50 rounded-lg">
                                <TypeIcon className="h-5 w-5 text-purple-600" />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">{design.title}</div>
                                <div className="text-xs text-gray-500">{design.file_format} • {design.dimensions}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{design.client_name || `Client #${design.client_id || 'N/A'}`}</div>
                            <div className="text-xs text-gray-500">{design.project_name || 'No project'}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getDesignTypeBadge(design.design_type)}`}>
                              {design.design_type || 'Design'}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">{getStatusBadge(design.status)}</td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{formatFileSize(design.file_size)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center space-x-2">
                              <button onClick={() => handlePreview(design)} className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded" title="Preview"><Eye className="h-4 w-4" /></button>
                              <button onClick={() => handleDownload(design)} className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded" title="Download"><Download className="h-4 w-4" /></button>
                              <button onClick={() => handleEdit(design)} className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded" title="Edit"><Edit className="h-4 w-4" /></button>
                              <button onClick={() => handleDelete(design)} className="text-gray-600 hover:text-red-600 p-1 hover:bg-red-50 rounded" title="Delete"><Trash2 className="h-4 w-4" /></button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              )}
            </div>
            );
          })}
        </div>
      )}

      {/* Empty State */}
      {filteredItems.length === 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="text-center">
            <Palette className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No designs found</h3>
            <p className="mt-1 text-sm text-gray-500">
              {activeFilterCount > 0
                ? 'Try adjusting your filters'
                : 'Get started by uploading your first design asset'}
            </p>
            {activeFilterCount === 0 && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="mt-4 inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="h-5 w-5 mr-2" />
                Upload Design
              </button>
            )}
          </div>
        </div>
      )}

      {/* Create Design Modal - Full Screen */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-white z-50 overflow-auto">
          <div className="min-h-screen">
            {/* Header with Close Button */}
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-gray-900">Upload Design Assets</h2>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  loadDesignContent(); // Refresh data after creation
                }}
                className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
              >
                Close
              </button>
            </div>

            {/* Render Design Asset Creation Component */}
            <div className="p-6">
              <DesignAssetCreation />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
